/**
 * /dashboard/accounting/contacts/$id — Contact detail (G_ACC3b).
 *
 * Layout:
 *   1. Header strip — name, kind badge, archived state, archive button
 *   2. Identifiers grid (GSTIN / PAN / TAN / State / MSME / TDS)
 *   3. Contact details (email / phone)
 *   4. Edit mode (toggled inline) — re-uses ContactForm with `contact`
 *      preloaded
 *
 * Edit mode lives inline rather than in a modal so the page stays a
 * single column on mobile (the form is too tall to feel right inside
 * a 360px-wide Dialog).
 *
 * Migration note (Phase 3e — 2026-06-01): port of
 * frontend/src/app/(dashboard)/dashboard/accounting/contacts/[id]/page.tsx.
 */

import { useState } from "react";
import { createFileRoute, Link , useParams} from "@tanstack/react-router";
import { ArrowLeft, ChevronRight, Pencil, X } from "lucide-react";
import { toApiError } from "@/lib/api";
import { useContact } from "@/hooks/use-accounting";
import {
  CONTACT_KIND_LABELS,
  INDIAN_STATE_LABELS,
  TDS_SECTION_LABELS,
  type Contact,
  type IndianStateCode,
  type TDSSection,
} from "@/types/accounting";
import { AccountingModuleShell } from "@/components/shared/accounting-module-shell";
import { ContactForm } from "@/components/accounting/contact-form";
import { ArchiveContactButton } from "@/components/accounting/archive-contact-button";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";
import { formatDateTime } from "@/lib/format";

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/accounting/contacts/$id",
)({
  component: ContactDetailRoute,
});

function DetailSkeleton() {
  return (
    <div className="space-y-4">
      <Skeleton className="h-24 w-full" />
      <Skeleton className="h-48 w-full" />
    </div>
  );
}

function FieldRow({
  label,
  value,
  mono,
}: Readonly<{
  label: string;
  value?: string | null;
  mono?: boolean;
}>) {
  return (
    <div className="space-y-1">
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </p>
      <p
        className={
          mono ? "font-mono text-sm text-gray-900" : "text-sm text-gray-900"
        }
      >
        {value && value.trim() !== "" ? value : "—"}
      </p>
    </div>
  );
}

function ContactDetailView({
  contact,
  onEdit,
}: Readonly<{ contact: Contact; onEdit: () => void }>) {
  const stateLabel = contact.state_code
    ? (INDIAN_STATE_LABELS[contact.state_code as IndianStateCode] ??
      contact.state_code)
    : null;
  const tdsLabel = contact.default_tds_section
    ? (TDS_SECTION_LABELS[contact.default_tds_section as TDSSection] ??
      contact.default_tds_section)
    : null;

  return (
    <div className="space-y-5">
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <CardTitle className="text-xl">{contact.name}</CardTitle>
              <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                <Badge variant="outline" className="font-medium">
                  {CONTACT_KIND_LABELS[contact.kind]}
                </Badge>
                {contact.is_archived && (
                  <Badge
                    variant="outline"
                    className="border-transparent bg-muted text-muted-foreground"
                  >
                    Archived
                  </Badge>
                )}
                {contact.msme_udyam && (
                  <Badge
                    variant="outline"
                    className="border-amber-300 bg-amber-50 text-amber-900"
                  >
                    MSME (45-Day Clock)
                  </Badge>
                )}
                <span>Updated {formatDateTime(contact.updated_at)}</span>
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                className="gap-1.5"
                onClick={onEdit}
              >
                <Pencil aria-hidden className="h-3.5 w-3.5" />
                Edit
              </Button>
              {!contact.is_archived && (
                <ArchiveContactButton contact={contact} />
              )}
            </div>
          </div>
        </CardHeader>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Tax & Identifiers</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <FieldRow label="GSTIN" value={contact.gstin} mono />
          <FieldRow label="State" value={stateLabel} />
          <FieldRow label="PAN" value={contact.pan} mono />
          <FieldRow label="TAN" value={contact.tan} mono />
          <FieldRow
            label="Udyam Registration No."
            value={
              contact.udyam_registration_no ??
              (contact.msme_udyam
                ? `${contact.msme_udyam} (old note — not a valid Udyam number)`
                : null)
            }
            mono
          />
          <FieldRow label="Default TDS Section" value={tdsLabel} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Contact Details</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          <FieldRow label="Email" value={contact.email} />
          <FieldRow label="Phone" value={contact.phone} />
          {contact.kind === "employee" && (
            <FieldRow
              label="Linked Employee ID"
              value={contact.user_id ? `User #${contact.user_id}` : null}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function ContactDetailRoute() {
  const { tenantSlug } = useParams({ strict: false }) as { tenantSlug: string };
  const { id } = Route.useParams();
  const numId = Number(id);
  const validId = Number.isFinite(numId) && numId > 0;
  const q = useContact(numId, validId);

  const [editing, setEditing] = useState(false);

  return (
    <AccountingModuleShell
      title={q.data?.name ?? "Contact"}
      actions={
        <Button asChild variant="outline" className="gap-1.5 lg:hidden">
          <Link to="/app/$tenantSlug/accounting/contacts" params={{ tenantSlug }}>
            <ArrowLeft aria-hidden className="h-4 w-4" />
            Back
          </Link>
        </Button>
      }
    >
      {!validId && (
        <Alert variant="destructive">
          <AlertTitle>Invalid contact id</AlertTitle>
          <AlertDescription>
            The URL doesn&apos;t look like a valid contact id.
          </AlertDescription>
        </Alert>
      )}

      {validId && q.isLoading && <DetailSkeleton />}

      {validId && q.isError && (
        <Alert variant="destructive">
          <AlertTitle>Could not load this contact</AlertTitle>
          <AlertDescription>
            {toApiError(q.error).message}{" "}
            <Link
              to="/app/$tenantSlug/accounting/contacts" params={{ tenantSlug }}
              className="ml-1 inline-flex items-center gap-1 font-medium text-primary underline-offset-4 hover:underline"
            >
              Back to Contacts
              <ChevronRight aria-hidden className="h-4 w-4" />
            </Link>
          </AlertDescription>
        </Alert>
      )}

      {validId && q.data && !editing && (
        <ContactDetailView
          contact={q.data}
          onEdit={() => setEditing(true)}
        />
      )}

      {validId && q.data && editing && (
        <Card className="max-w-3xl">
          <CardHeader>
            <div className="flex items-center justify-between gap-2">
              <CardTitle className="text-base">Edit Contact</CardTitle>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setEditing(false)}
                aria-label="Close edit form"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <ContactForm
              contact={q.data}
              onSuccess={() => setEditing(false)}
              onCancel={() => setEditing(false)}
            />
          </CardContent>
        </Card>
      )}
    </AccountingModuleShell>
  );
}
